import React, { useState } from 'react';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';

// Public topojson world atlas — countries at 110m resolution
const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

// ISO 3166-1 numeric codes of countries where Karma operates (painted navy on the map)
const DESTINATION_COUNTRY_IDS = new Set([
  '826', // United Kingdom (England — Salford Hall)
  '276', // Germany (Bavaria)
  '380', // Italy (Borgo di Colleoli)
  '818', // Egypt (Karnak)
  '704', // Vietnam (Song Hoai / Duyên)
  '116', // Cambodia (Bayon Siem Reap)
  '764', // Thailand (Panalee / Sawet / Boat Lagoon)
  '360', // Indonesia (Kandara)
]);

// Real lat/lng coordinates for each destination
const destinationsGeo = [
  { id: 'england',  name: 'England',   coords: [ -1.90, 52.10 ], labelSide: 'left' },
  { id: 'bavaria',  name: 'Germany',   coords: [ 11.87, 47.73 ], labelSide: 'right' },
  { id: 'borgo',    name: 'Italy',     coords: [ 11.25, 43.30 ], labelSide: 'left', labelYOffset: 6 },
  { id: 'egypt',    name: 'Egypt',     coords: [ 32.63, 25.68 ], labelSide: 'right' },
  { id: 'vietnam',  name: 'Vietnam',   coords: [ 108.34, 15.88 ], labelSide: 'right' },
  { id: 'cambodia', name: 'Cambodia',  coords: [ 103.85, 13.36 ], labelSide: 'right', labelYOffset: 14 },
  { id: 'thailand', name: 'Thailand',  coords: [ 100.00, 9.53 ],  labelSide: 'left',  labelYOffset: 6 },
  { id: 'bali',     name: 'Indonesia', coords: [ 115.19, -8.65 ], labelSide: 'right' },
];

const DiscoverWorld = () => {
  const [hovered, setHovered] = useState(null);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section id="discover" className="bg-warm py-24 md:py-32 border-y border-divider">
      <div className="max-w-[1300px] mx-auto px-6 md:px-10">
        <div className="text-center reveal">
          <div className="gold-divider mb-5">
            <span className="chapter-badge text-gold uppercase">Discover the World</span>
          </div>
          <h2 className="font-serif-display text-navy text-4xl md:text-5xl leading-[1.1] font-medium">
            From inspiration to unforgettable.
          </h2>
          <p className="mt-4 font-serif-editorial text-navy/70 text-xl">
            Each destination offers its own story. Which one will be yours?
          </p>
        </div>

        <div className="mt-14 relative reveal">
          <div
            className="relative w-full overflow-hidden"
            style={{
              background: 'radial-gradient(1200px 500px at 50% 45%, #FBF7EE 0%, #F1E7CE 65%, #E7DBB4 100%)',
            }}
          >
            <ComposableMap
              projection="geoEqualEarth"
              projectionConfig={{ scale: 175, center: [30, 20] }}
              style={{ width: '100%', height: 'auto', pointerEvents: 'auto' }}
            >
                <Geographies geography={GEO_URL}>
                  {({ geographies }) =>
                    geographies.map((geo) => {
                      const isDestination = DESTINATION_COUNTRY_IDS.has(String(geo.id));
                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          style={{
                            default: {
                              fill: isDestination ? '#0F2340' : '#E9DAB0',
                              stroke: isDestination ? '#0F2340' : '#B8934A',
                              strokeWidth: isDestination ? 0.6 : 0.4,
                              outline: 'none',
                            },
                            hover: {
                              fill: isDestination ? '#1A3358' : '#E4D19F',
                              stroke: isDestination ? '#0F2340' : '#B8934A',
                              strokeWidth: isDestination ? 0.7 : 0.5,
                              outline: 'none',
                            },
                            pressed: {
                              fill: isDestination ? '#0F2340' : '#E4D19F',
                              outline: 'none',
                            },
                          }}
                        />
                      );
                    })
                  }
                </Geographies>

                {destinationsGeo.map((d) => {
                  const isHover = hovered === d.id;
                  return (
                    <Marker
                      key={d.id}
                      coordinates={d.coords}
                      onClick={() => scrollTo(d.id)}
                      onMouseEnter={() => setHovered(d.id)}
                      onMouseLeave={() => setHovered(null)}
                      style={{
                        default: { cursor: 'pointer' },
                        hover: { cursor: 'pointer' },
                        pressed: { cursor: 'pointer' },
                      }}
                    >
                      {/* Glow ring */}
                      <circle r={isHover ? 10 : 6} fill="#B8934A" opacity={0.15} />
                      <circle r={isHover ? 6 : 4} fill="#B8934A" opacity={0.3} />
                      {/* Solid centre */}
                      <circle
                        r={isHover ? 4 : 3}
                        fill="#B8934A"
                        stroke="#FBF7EE"
                        strokeWidth={1.2}
                      />
                      {/* Label */}
                      <g transform={`translate(${d.labelSide === 'left' ? -10 : 10}, ${d.labelYOffset ?? -14})`}>
                        <rect
                          x={d.labelSide === 'left' ? -(d.name.length * 6 + 10) : 0}
                          y={-8}
                          width={d.name.length * 6 + 10}
                          height={14}
                          fill={isHover ? '#0F2340' : '#FBF7EE'}
                          stroke="#B8934A"
                          strokeWidth={0.5}
                          opacity={0.95}
                        />
                        <text
                          textAnchor={d.labelSide === 'left' ? 'end' : 'start'}
                          x={d.labelSide === 'left' ? -5 : 5}
                          y={2}
                          style={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: 7,
                            fontWeight: 500,
                            letterSpacing: '0.12em',
                            textTransform: 'uppercase',
                            fill: isHover ? '#FBF7EE' : '#0F2340',
                            pointerEvents: 'none',
                          }}
                        >
                          {d.name}
                        </text>
                      </g>
                    </Marker>
                  );
                })}
            </ComposableMap>
          </div>

          {/* Chips — mobile-friendly quick nav */}
          <div className="mt-10 flex flex-wrap justify-center gap-2">
            {destinationsGeo.map((c) => (
              <button
                key={c.id}
                onClick={() => scrollTo(c.id)}
                className="chapter-badge uppercase px-4 py-2 border border-divider text-navy hover:bg-navy hover:text-warm transition-colors duration-300"
              >
                {c.name}
              </button>
            ))}
          </div>

          <p className="mt-6 text-center font-serif-editorial text-navy/60 italic text-sm">
            Tap a country to explore its Karma Experience chapter.
          </p>
        </div>
      </div>
    </section>
  );
};

export default DiscoverWorld;
